import React, { useCallback, useState } from "react";
import { Button, Space, Tooltip, Typography } from "antd";
import { getItemPropertyValue, Item } from "../../data-types";
import { VpdList } from "../vpd-list";
import { Icons } from "../vpd-icons/icons";
import {
  AlertMessagePopover,
  AlertMessageBannerProps,
  newErrorAlertMessage,
} from "../alert-message";
import { DndContext, useSensor, useSensors, PointerSensor, DragOverlay } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableItem, DraggableHandle } from "../draggable-list";
import { ExclamationCircleOutlined, LockFilled } from "@ant-design/icons";
import { WorkspaceItemButton } from "./workspace-item-button";
import useModal from "antd/lib/modal/useModal";

export type WorkspaceListProps<T extends Item> = {
  itemKeyProperty: string;
  itemTextProperty: string;
  loading: boolean;
  items: T[];
  deleteItem: (item: T) => Promise<void>;
  saveItem: (item: T, changedText: string) => Promise<void>;
  onReorder: (activeId: number, overId: number) => void;
  workspaceItemType: string;
  renderExtraActions?: (item: T) => React.ReactNode;
};

export const WorkspaceList = <T extends Item>({
  itemKeyProperty,
  itemTextProperty,
  items,
  loading,
  deleteItem,
  saveItem,
  onReorder,
  workspaceItemType,
  renderExtraActions,
}: WorkspaceListProps<T>) => {
  const sensors = useSensors(useSensor(PointerSensor));
  const [activeItem, setActiveItem] = useState<T | null>(null);
  return (
    <DndContext
      onDragStart={({ active }) => {
        if (parseInt(active.id) <= 0) return;
        setActiveItem(items.find((p) => p.id.toString() === active.id)!);
      }}
      onDragEnd={({ active, over }) => {
        const activeId = parseInt(active.id);
        const overId = over ? parseInt(over.id) : null;

        if (activeId >= 0 && overId && overId >= 0) {
          onReorder(activeId, overId);
        }
      }}
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={items.map((p) => p.id.toString())}
        strategy={verticalListSortingStrategy}
      >
        <VpdList
          itemKeyProperty={itemKeyProperty}
          itemTextProperty={itemTextProperty}
          loading={loading}
          split={false}
          items={items}
          renderItem={(item, index) =>
            renderItem(
              item,
              index,
              itemKeyProperty,
              itemTextProperty,
              deleteItem,
              saveItem,
              workspaceItemType,
              renderExtraActions
            )
          }
          listStyle="primary"
          labels={{
            emptyText: "No items available.",
            loadingText: "Loading items...",
          }}
        />
      </SortableContext>
      <DragOverlay>
        {activeItem ? (
          <VpdList
            itemKeyProperty={itemKeyProperty}
            itemTextProperty={itemTextProperty}
            loading={loading}
            split={false}
            items={[activeItem]}
            renderItem={(item) => (
              <VpdList.Item
                key={getItemPropertyValue(item, itemKeyProperty).toString()}
                content={
                  <Typography.Text editable={false}>
                    {getItemPropertyValue(item, itemTextProperty)}
                  </Typography.Text>
                }
                contentSuffix={
                  <Space size={1}>
                    {!item.mandatory ? (
                      <>
                        <Button icon={Icons.Edit} shape="circle" type="link" />
                        <Button icon={Icons.Delete} shape="circle" type="link" />
                      </>
                    ) : (
                      <LockFilled />
                    )}
                  </Space>
                }
                contentPrefix={<DraggableHandle />}
              />
            )}
            listStyle="primary"
            labels={{
              emptyText: "No items available.",
              loadingText: "Loading items...",
            }}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

type WorkspaceListItemState = {
  editing: boolean;
  saving: boolean;
  deleting: boolean;
  errorAlertProps?: AlertMessageBannerProps;
};
function newInitialState(editing?: boolean): WorkspaceListItemState {
  return {
    editing: editing === true || false,
    saving: false,
    deleting: false,
    errorAlertProps: undefined,
  };
}

export type WorkspaceListItemProps<T extends Item> = {
  item: T;
  itemKeyProperty: string;
  itemTextProperty: string;
  initialEditing?: boolean;
  deleteItem: (item: T) => Promise<void>;
  saveItem: (item: T, changedText: string) => Promise<void>;
  workspaceItemType: string;
  renderExtraActions?: (item: T) => React.ReactNode;
};

export const WorkspaceListItem = <T extends Item>({
  item,
  itemKeyProperty,
  itemTextProperty,
  initialEditing,
  deleteItem,
  saveItem,
  workspaceItemType,
  renderExtraActions,
}: WorkspaceListItemProps<T>) => {
  const [state, setState] = useState(() => newInitialState(initialEditing));
  const { editing, saving, deleting, errorAlertProps } = state;
  const key = getItemPropertyValue(item, itemKeyProperty);
  const text = getItemPropertyValue(item, itemTextProperty);
  const [modal, contextHolder] = useModal();

  const onEdit = () => setState({ ...state, editing: true });

  const confirmDelete = useCallback(
    (cb: () => void) => {
      modal.confirm({
        width: 600,
        icon: <ExclamationCircleOutlined />,
        content: (
          <Typography.Text>{`Are you sure you want to delete this ${workspaceItemType}?`}</Typography.Text>
        ),
        onOk: async function () {
          cb();
        },
        onCancel() {},
      });
    },
    [workspaceItemType]
  );

  const onChange = async (value: string) => {
    if (value.trim() === "") {
      return;
    }

    if (!saving && value === text) {
      // No changes made
      setState({ ...state, editing: false });
      return;
    }

    setState({ ...state, saving: true });
    try {
      await saveItem(item, value);
      setState({ ...state, editing: false, saving: false });
    } catch (error) {
      const errorAlertProps = newErrorAlertMessage(error);
      setState({ ...state, saving: false, errorAlertProps });
    }
  };

  const onDelete = async () => {
    confirmDelete(async () => {
      setState({ ...state, deleting: true });
      try {
        await deleteItem(item);
        setState({ ...state, deleting: false });
      } catch (error) {
        const errorAlertProps = newErrorAlertMessage(error);
        setState({ ...state, saving: false, errorAlertProps });
      }
    });
  };

  const editable =
    editing || text === ""
      ? { editing: true, onChange: onChange, ellipsis: true, icon: null }
      : false;
  return (
    <>
      {contextHolder}
      <SortableItem id={item.id.toString()} key={item.id}>
        {({ attributes, listeners }) => (
          <VpdList.Item
            key={key.toString()}
            content={<Typography.Text editable={editable}>{text}</Typography.Text>}
            contentSuffix={
              <Space size={1}>
                {errorAlertProps ? (
                  <AlertMessagePopover {...errorAlertProps}>{Icons.Error}</AlertMessagePopover>
                ) : (
                  <div style={{ width: "22px" }}>&nbsp;</div>
                )}
                {saving ? (
                  <Tooltip placement="top" title="Saving...">
                    {Icons.Loading}
                  </Tooltip>
                ) : (
                  !item.mandatory && (
                    <Button
                      icon={Icons.Edit}
                      shape="circle"
                      type="link"
                      onClick={onEdit}
                      disabled={deleting}
                    />
                  )
                )}
                {renderExtraActions ? renderExtraActions(item) : null}
                {deleting ? (
                  <Tooltip placement="top" title="Deleting...">
                    {Icons.Loading}
                  </Tooltip>
                ) : (
                  <WorkspaceItemButton
                    workspaceItemType={workspaceItemType}
                    mandatory={item.mandatory}
                    icon={Icons.Delete}
                    disabled={saving}
                    onClick={onDelete}
                  />
                )}
              </Space>
            }
            contentPrefix={<DraggableHandle {...attributes} {...listeners} />}
          />
        )}
      </SortableItem>
    </>
  );
};

function renderItem<T extends Item>(
  item: T,
  index: number,
  itemKeyProperty: string,
  itemTextProperty: string,
  deleteItem: (item: T) => Promise<void>,
  saveItem: (item: T, changedText: string) => Promise<void>,
  workspaceItemType: string,
  renderExtraActions?: (item: T) => React.ReactNode
) {
  return (
    <WorkspaceListItem
      workspaceItemType={workspaceItemType}
      item={item}
      itemKeyProperty={itemKeyProperty}
      itemTextProperty={itemTextProperty}
      deleteItem={deleteItem}
      saveItem={saveItem}
      renderExtraActions={renderExtraActions}
    />
  );
}
